import type { JSX } from 'react'

import { compareLensOptions } from '@lensmira/domain'

import { Ltr, useTranslation } from '../i18n.js'
import { formatMoneyForDisplay, parseMinorUnits } from '../money.js'
import type { LensOptionData } from './LensChoice.js'

/**
 * Two or three lens options side by side. §6.6 `DSP-006`.
 *
 * `DSP-006` `SHOULD`: "comparison view showing two or three lens options side by side with total
 * price, so the conversation is about trade-offs rather than a single number."
 *
 * **The arithmetic is not here.** `compareLensOptions` in `packages/domain` does the adding, in
 * `bigint` minor units with no rounding, and this renders what it returns. The delta from the
 * cheapest is computed there too, so no column works out its own difference: a column that did
 * would be a second place the arithmetic can disagree with itself.
 *
 * **The total excludes order-level discount and tax**, per `decisions/0018`. Those run at steps 9
 * and 10 of the order chain and an order discount is apportioned back to lines, so a total computed
 * here that included either would be a second implementation of `MR-03` free to disagree with the
 * invoice the customer is handed. The heading says so, because a figure the screen admits is before
 * discount is better than one that is quietly 400 rupees out.
 *
 * **Absent and null stay different**, as they do in `LensChoice`. An absent `costMinor` means this
 * reader may not see cost (`ROLE-001a`, a per-branch grant), and the row is not rendered at all; a
 * null one means no connected supplier quoted that lens, and it says so. Rendering a blank cell for
 * a withheld figure would read as missing data rather than as a permission.
 */

export interface LensComparisonProps {
  readonly options: readonly LensOptionData[]
  readonly currency: string
  /** The non-lens lines already on the order, in minor units. `0n` is a re-glaze. */
  readonly otherLinesMinor: bigint
  readonly chosenProductId?: string
  readonly onChoose: (choice: { readonly productId: string; readonly name: string }) => void
}

export function LensComparison({
  options,
  currency,
  otherLinesMinor,
  chosenProductId,
  onChoose,
}: LensComparisonProps): JSX.Element | null {
  const { t } = useTranslation()

  let columns
  try {
    columns = compareLensOptions(
      options.map((o) => ({
        productId: o.productId,
        name: o.name,
        // `parseMinorUnits` and never `Number`: the wire carries minor units as a string precisely
        // so a large order does not lose a unit through a double (Appendix D).
        priceMinor: parseMinorUnits(o.priceMinor),
        warnings: o.warnings,
      })),
      otherLinesMinor,
    )
  } catch {
    // Fewer than two or more than three ticked. The caller's button is disabled outside that range,
    // so reaching this is a bug rather than a state to explain, and a thrown error would take the
    // lens list down with it.
    return null
  }

  const amount = (minor: bigint): string => formatMoneyForDisplay(minor, currency)
  const byId = new Map(options.map((o) => [o.productId, o]))
  // Cost and margin are gated per caller, not per lens, so one absent field means the whole row is
  // withheld. Asked of the first column rather than any, so a null does not read as an absence.
  const showsCost = options.every((o) => o.costMinor !== undefined)
  const showsMargin = options.every((o) => o.marginMinor !== undefined)

  return (
    <section className="lm-lens-compare" aria-labelledby="lens-compare-heading">
      <h3 id="lens-compare-heading" className="lm-lens-compare__heading">
        {t('lensChoice.compareHeading')}
      </h3>
      {/* Said out loud, because the number a dispenser reads to a customer has to be one they can
          stand behind when the invoice differs from it. */}
      <p className="lm-hint">{t('lensChoice.compareExcludes')}</p>

      <table className="lm-lens-compare__table">
        <thead>
          <tr>
            <th scope="col">{t('lensChoice.compareRow')}</th>
            {columns.map((column) => (
              <th key={column.productId} scope="col" dir="auto">
                {column.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">{t('lensChoice.compareLensPrice')}</th>
            {columns.map((column) => (
              <td key={column.productId}>
                {/* A figure never mirrors, whatever the page direction. `DESIGN-TOKENS.md` §6. */}
                <Ltr>{amount(column.lensMinor)}</Ltr>
              </td>
            ))}
          </tr>
          <tr>
            <th scope="row">{t('lensChoice.compareOtherLines')}</th>
            {columns.map((column) => (
              <td key={column.productId}>
                <Ltr>{amount(column.otherLinesMinor)}</Ltr>
              </td>
            ))}
          </tr>
          <tr className="lm-lens-compare__total">
            <th scope="row">{t('lensChoice.compareTotal')}</th>
            {columns.map((column) => (
              <td key={column.productId}>
                <Ltr>{amount(column.totalMinor)}</Ltr>
              </td>
            ))}
          </tr>
          <tr>
            <th scope="row">{t('lensChoice.compareDifference')}</th>
            {columns.map((column) => (
              <td key={column.productId}>
                {/* The cheapest says so in words rather than by being blank: a blank cell in a
                    difference row reads as a figure nobody computed. */}
                {column.isCheapest ? (
                  t('lensChoice.compareCheapest')
                ) : (
                  <Ltr>{amount(column.deltaFromCheapestMinor)}</Ltr>
                )}
              </td>
            ))}
          </tr>
          {showsCost ? (
            <tr>
              <th scope="row">{t('lensChoice.compareCost')}</th>
              {columns.map((column) => {
                const cost = byId.get(column.productId)?.costMinor
                return (
                  <td key={column.productId}>
                    {cost === null || cost === undefined ? (
                      t('lensChoice.costUnquoted')
                    ) : (
                      <Ltr>{amount(parseMinorUnits(cost))}</Ltr>
                    )}
                  </td>
                )
              })}
            </tr>
          ) : null}
          {showsMargin ? (
            <tr>
              <th scope="row">{t('lensChoice.compareMargin')}</th>
              {columns.map((column) => {
                const margin = byId.get(column.productId)?.marginMinor
                return (
                  <td key={column.productId}>
                    {margin === null || margin === undefined ? (
                      t('lensChoice.costUnquoted')
                    ) : (
                      <Ltr>{amount(parseMinorUnits(margin))}</Ltr>
                    )}
                  </td>
                )
              })}
            </tr>
          ) : null}
          <tr>
            <th scope="row">{t('lensChoice.compareWarnings')}</th>
            {columns.map((column) => (
              <td key={column.productId}>
                {column.warnings.length === 0 ? (
                  t('lensChoice.compareNoWarnings')
                ) : (
                  <ul className="lm-lens-compare__warnings">
                    {column.warnings.map((finding, index) => (
                      <li key={`${column.productId}-${finding.code}-${index}`}>
                        {/* The server's sentence, which names the measurement and the limit. */}
                        {finding.message}
                      </li>
                    ))}
                  </ul>
                )}
              </td>
            ))}
          </tr>
          <tr>
            <th scope="row">{t('lensChoice.compareRowChoose')}</th>
            {columns.map((column) => (
              <td key={column.productId}>
                <button
                  type="button"
                  className="lm-button lm-button--secondary"
                  onClick={() => {
                    onChoose({ productId: column.productId, name: column.name })
                  }}
                >
                  {column.productId === chosenProductId
                    ? t('lensChoice.chosen', { name: column.name })
                    : t('lensChoice.choose', { name: column.name })}
                </button>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </section>
  )
}
